import "./Common/Lines/fabricArrow.js";
import "./Common/Lines/fabricTwoArrow.js";

import { styles } from "./Resources/drawing-styles.js";
import { mimeType, saveDrawing, getDrawingById } from "../../data/images.js";
import { DrawingHistory } from "./Common/history.js";
import "./top-toolbar.js";

import { DrawColorsModule } from "./Common/colors.js";
import { DrawShapesModuleContainer } from "./Common/Shapes/shapes-module-container.js";
import { DrawBackgroundImageModule } from "./Common/background-images.js";

import {
	loadFullsizeImage,
	getCanvasA4Size,
	clearSelection,
	resetModulesMetadata,
	getCanvasSignatureSize
} from "./helpers.js";
import { DrawTextModule } from "./Common/text.js";
import { InspxDrawingTopToolbar } from "./top-toolbar.js";
import { DrawingScroll } from "./Common/scroll.js";
import { DrawingZoom } from "./Common/zoom.js";
import { DrawSegmentsModuleContainer } from "./RPS/Segments/segments-module-container.js";
import { DrawGestureAndMouseEvents } from "./Common/gesture-and-mouse-events.js";
import { colorTheme } from "../../styles/color-theme.js";
import { InspxDrawingMainToolbar } from "./main-toolbar.js";
import { DrawPhotoNumberContainer } from "./RPS/photo-numbers/photo-number-container.js";
import { DrawLegendModule } from "./RPS/legend.js";
import { DrawTagsAndLabelsContainer } from "./RPS/tags-labels/tags-labels-container.js";
import { DrawLinesModuleContainer } from "./Common/Lines/lines-module-container.js";
import { PolygonModuleContainer } from "./Common/Polygon/polygon-container.js";
import { PolygonModule } from "./Common/Polygon/polygon-module";
import { DrawFreedrawModuleContainer } from "./Common/FreeDraw/freedraw-module-container.js";
import { DrawSegmentsModule } from "./RPS/Segments/segments-module.js";
import { DrawSelector } from "./Common/selector/selector.js";
import { DrawShapeBaseModule } from "./ModuleBase/shape-base-module.js";
import { DrawShapeBaseAdvancedModule } from "./ModuleBase/shape-base-advanced-module.js"
import { DrawSquaresModule } from "./RPS/tags-labels/squares-module.js";
import { DrawImageModuleContainer } from "./Common/ImageDraw/image-module-container.js";
import { DrawImageModule } from "./Common/ImageDraw/image-module.js";
import { DrawGridModule } from "./Common/grid.js";
import { LitElement, html } from "lit-element";
import { SelectorModule } from "./Common/selector/selector-module.js";
import { addMetadata } from "./ModuleBase/ModuleMetadata.js";

export class DrawingTool extends LitElement {
	render() {
		return html`
            ${colorTheme} ${styles} ${this.topToolbar}
			<style>
				:host([is-signature]) inspx-drawing-main-toolbar,
				:host([is-signature]) .secondary-toolbar {
                    display: none;
                }
			</style>
            <input type="file" id="fileInput" @change="${this._fileSelected.bind(this)}" accept=".json" style="display:none;">
			<div class="container">
				${this.mainToolbar}
			
				<div class="canvas-wrapper" id="canvasWrapper">
					<canvas id="c"></canvas>
				</div>
				<div class="secondary-toolbar">
					${this.activeModule}
				</div>
			</div>`;
	}

	selectDrawingModule(m) {
		if (m.current && m.current.Deactivate) m.current.Deactivate();
		this.resetCanvasBehavior();
		this.activeModule = m.next.Activate();
		this.previousActiveModule = m.current;
	}

	resetCanvasBehavior() {
		this.canvas.isDragging = false;
		this.canvas.selection = false;
		this.canvas.isDrawingMode = false;
		this.canvas.freeDrawingBrush = new fabric.PencilBrush(this.canvas);
		clearSelection(this.canvas);
		this.canvas.renderAll();
	}

	initModulesFlow() {
		this.canvas.on("module-draw", e => {
			switch (true) {
				case (e.target instanceof DrawSegmentsModule):
				case (e.target instanceof DrawShapeBaseModule):
				case (e.target instanceof DrawShapeBaseAdvancedModule):
				case (e.target instanceof DrawSquaresModule):
				case (e.target instanceof DrawImageModule):
				case (e.target instanceof PolygonModule):
					this.activateModule(this.selector);
					break;
			}
		});
	}

	constructor(isSignature) {
		super();

		this.drawingModules = [];
		this._hotkeysPreventersCount = 0;
		this.__keyDown = (e) => this._keyDown(e);
		this.__multiKeydown = (e) => this._multiKeydown(e);
		this.addEventListener("drawing-settings-open", () => { this._hotkeysPreventersCount++; });
		this.addEventListener("drawing-settings-close", () => { this._hotkeysPreventersCount--; });
		this.addEventListener("activate-previous-module", () => {
			this.mainToolbar.shadowRoot.querySelector(`label[name=${this.previousActiveModule.MainTb.value}]`).click()
		});
		this.map = {};
		this.isSignature = isSignature || false;
	}

	_multiKeydown(e) {
		e = e || event; // to deal with IE
		this.map[e.keyCode] = e.type == 'keydown';
		if (this.map[17] == true && this.map[67] == true) { // ctrl= 17, c= 67
			let selector = new SelectorModule(this.canvas);
			let activeObject = selector.canvas._activeObject;
			if (activeObject == undefined) return;
			selector.makeCopy(activeObject);
		}
	}

	connectedCallback() {
		super.connectedCallback();
		document.addEventListener('keydown', this.__keyDown);
		document.addEventListener('keydown', this.__multiKeydown);
		document.addEventListener('keyup', this.__multiKeydown);
		document.body.classList.add('freeze-scroll');
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		document.removeEventListener('keydown', this.__keyDown);
		document.removeEventListener('keydown', this.__multiKeydown);
		document.removeEventListener('keyup', this.__multiKeydown);
		document.body.classList.remove('freeze-scroll');
		this.zoomModule.realeaseResources();
	}

	_keyDown(e) {
		if (this._hotkeysPreventersCount > 0) return;

		if (e.keyCode === 8) { //backspace
			this.topToolbar.undo();
		} else if (e.keyCode === 46) { //delete
			this.topToolbar.delete();
		} else if (e.keyCode === 27) { //esc
			this.canvas
				.discardActiveObject()
				.renderAll();
		}
	}

	static get properties() {
		return {
			drawing: { type: Object },
			isSignature: { type: Boolean },
			drawingIdToLoad: { type: Number },
			imageIdToLoad: { type: Number },
			selectedColor: { type: String },
			activeModule: { type: Object },
			drawingModules: { type: Array }
		};
	}

	firstUpdated() {
		var canvas = new fabric.Canvas(this.shadowRoot.getElementById("c"), { preserveObjectStacking: true });

		fabric.Object.prototype.transparentCorners = false;
		canvas.backgroundColor = "white";

		this.canvas = canvas;

		this.canvas.on({
			"object:added": this.objectAddedHandler.bind(this),
			"object:modified": this.objectModifiedHandler.bind(this),
			"text:editing:entered": () => { this._hotkeysPreventersCount++; },
			"text:editing:exited": (obj) => {
				this._hotkeysPreventersCount--;
				let o = obj.target;
				if (o && o.moduleName && o.moduleState) {
					addMetadata(o, { ...o.moduleState, text: o.text });
				}
			},
		});
		this.drawingHistory = new DrawingHistory(this.canvas);

		this.activateModule = mod => {
			this.mainToolbar.activateModule(mod);
		};

		let preInitModulesPromise = new Promise((resolve, reject) => {
			if (this.drawingIdToLoad) {
				getDrawingById(this.drawingIdToLoad).then(d => {
					this.drawing = d;
					this.drawingHistory.pause();
					this.canvas.loadFromJSON(this.drawing.drawing, () => {
						this.selectedBackground = Number(this.drawing.backgroundId);
						resetModulesMetadata(this.canvas);
						this.resetCanvasBehavior();
						this.drawingHistory.unpause();
						this.drawingHistory.save();
						resolve();
					});
				});
			} else if (this.imageIdToLoad) {
				this.drawingHistory.pause();
				loadFullsizeImage(
					this.imageIdToLoad,
					this.canvas,
					this,
					this.drawingHistory
				)
					.then(imgSize => {
						canvas.initialSize = { w: imgSize.w, h: imgSize.h }
						canvas.renderAll();
						this.drawingHistory.unpause();
						this.drawingHistory.save();
						resolve();
					})
			} else {
				canvas.initialSize = this.isSignature ? getCanvasSignatureSize() : getCanvasA4Size();
				canvas.setWidth(canvas.initialSize.w);
				canvas.setHeight(canvas.initialSize.h);
				this.legend = new DrawLegendModule(this.canvas);
				this.legend.showLegend().then(() => {
					resolve();
				});
			}
		});

		preInitModulesPromise.then(() => this.initModules());
	}

	initModules() {
		this.scrollModule = new DrawingScroll(this.canvas);
		this.zoomModule = new DrawingZoom(this.canvas);
		this.zoomModule.subscribe();
		this.zoomModule.setZoomToFitWindow();

		let a4 = getCanvasA4Size();
		let isA4 = a4.w === this.canvas.initialSize.w && a4.h === this.canvas.initialSize.h;

		if (isA4) {
			if (this.legend == undefined) {
				this.legend = new DrawLegendModule(this.canvas);
			}
			this.legend.updateState();
		}

		this.grid = new DrawGridModule(this.canvas, this.drawingHistory);
		this.grid.updateState();

		this.topToolbar = new InspxDrawingTopToolbar(
			this.canvas,
			this.drawingHistory,
			this.zoomModule,
			this.close.bind(this),
			this.legend,
			this.grid,
			this.download.bind(this),
			this.upload.bind(this)
		);
		if (this.isSignature) {
			this.setAttribute('is-signature', '');
			this.topToolbar.setAttribute('is-signature', '');
		}
		this.onClose = this.close.bind(this);

		let colorModule = new DrawColorsModule(() => {
			this.mainToolbar.requestUpdate();
		});
		let activeColorSelector = () => colorModule.activeColor;
		let freedraw = new DrawFreedrawModuleContainer(
			this.canvas,
			activeColorSelector
		);

		this.selector = new DrawSelector(this.canvas);
		this.drawingModules = [
			new DrawGestureAndMouseEvents(this.canvas, this.zoomModule),
			this.selector,
			freedraw,
			colorModule,
			new DrawTagsAndLabelsContainer(this.canvas),
			new DrawShapesModuleContainer(this.canvas, activeColorSelector, this.drawingHistory),
			new DrawLinesModuleContainer(this.canvas, activeColorSelector, this.drawingHistory),
			new PolygonModuleContainer(this.canvas, activeColorSelector, this.drawingHistory),
			new DrawBackgroundImageModule(this.canvas, this.drawingHistory),
			new DrawImageModuleContainer(this.canvas),
			new DrawTextModule(this.canvas, activeColorSelector),
			new DrawSegmentsModuleContainer(this.canvas),
			new DrawPhotoNumberContainer(this.canvas)
		];

		this.mainToolbar = new InspxDrawingMainToolbar(
			this.drawingModules,
			activeColorSelector,
			this.isSignature ? this.drawingModules.indexOf(freedraw) : 0
		);
		this.mainToolbar.onSelected = e => this.selectDrawingModule(e);
		this.initModulesFlow();
	}

	objectAddedHandler() {
		if (!this.canvas || this.canvas.isEmpty()) return;

		var size = this.canvas.size();
		var lastObject = this.canvas.item(size - 1);
		lastObject.set({
			cornerSize: 30,
			padding: 20
		});

		lastObject.selectable = this.canvas.selection;
		if (lastObject.neverSelect) {
			lastObject.selectable = false;
		}
		if (lastObject.moduleState) {
			if (lastObject.moduleState.neverSelect) {
				lastObject.selectable = false;
			}
			if (lastObject.moduleState.alwaysBack) {
				this.canvas.sendToBack(lastObject);
			}
		}
		if (this.legend && typeof this.legend.updateState === 'function') this.legend.updateState();
		if (this.grid && typeof this.grid.updateState === 'function') this.grid.updateState();
	}

	objectModifiedHandler() {
		this.canvas.forEachObject(o => {
			if (o.moduleState && o.moduleState.alwaysBack) {
				this.canvas.sendToBack(o);
			}
		});
	}

	close() {
		if (this.drawingHistory.isStart && !this.drawingHistory._isShifted) {
			this.remove();
			return;
		}

		this.zoomModule.zoom = 1;
		this.zoomModule.applyZoom();

		if (this.topToolbar._canvasGrid)
			this.canvas.remove(this.topToolbar._canvasGrid);

		//remove grid before saving
		var Grid = this.canvas._objects.filter(a => a.moduleName == "DrawGridModule")[0];
		if (Grid) { this.canvas.remove(Grid); }

		var image = new Image();
		image.onload = () => {
			var resizedCanvas = document.createElement("canvas");
			var resizedContext = resizedCanvas.getContext("2d");

			var horizontal = this.canvas.width > this.canvas.height;
			var ratio = this.canvas.height / 400;
			var w = this.canvas.width / ratio;
			var h = this.canvas.height / ratio;

			resizedCanvas.width = w;
			resizedCanvas.height = h;

			resizedContext.drawImage(image, 0, 0, w, h);
			resizedCanvas.toBlob(
				blob => {
					let cObj = this.canvas.toJSON();
					cObj.initialSize = this.canvas.initialSize;
					saveDrawing(
						this.drawingIdToLoad ? this.drawing.id : undefined,
						blob,
						INSP.APP.inspId,
						undefined,
						horizontal,
						mimeType,
						JSON.stringify(cObj),
						this.selectedBackground,
						this.isSignature ? "signature" : this.drawing ? this.drawing.propertyName : undefined
					).then(() => {
						this.remove();
						this.dispatchEvent(new CustomEvent('onDrawingToolClosed'));
						INSP.APP.reloadInspection();
					});
				},
				mimeType,
				1
			);
		};
		image.src = this.canvas.toDataURL();
	}

	download() {
		let s = this.canvas.toJSON()
		s.initialSize = this.canvas.initialSize
		const str = JSON.stringify(s)
		const data = this.encode(str);

		const blob = new Blob([data], { type: 'application/octet-stream' });
		const url = URL.createObjectURL(blob);
		var link = document.createElement('a');
		link.setAttribute('href', url);
		link.setAttribute('download', 'data.json');
		var event = document.createEvent('MouseEvents');
		event.initMouseEvent('click', true, true, window, 1, 0, 0, 0, 0, false, false, false, false, 0, null);
		link.dispatchEvent(event);
	}

	encode(s) {
		var out = [];
		for (var i = 0; i < s.length; i++) {
			out[i] = s.charCodeAt(i);
		}
		return new Uint8Array(out);
	}

	upload() {
		this.shadowRoot.getElementById("fileInput").click()
	}

	_fileSelected(e) {
		if (e.currentTarget.files.length === 0)
			return;
		const file = e.currentTarget.files[0];
		let reader = new FileReader();
		let text
		reader.onload = () => {
			text = reader.result;
			this.drawingHistory.pause();
			this.canvas.initialSize = JSON.parse(text).initialSize;
			this.zoomModule.setZoomToFitWindow();
			this.canvas.loadFromJSON(text, () => {
				resetModulesMetadata(this.canvas);
				this.resetCanvasBehavior();
				this.activateModule(this.selector);
				this.requestUpdate();
				this.legend.updateState();
				this.drawingHistory.unpause();
				this.drawingHistory.save();
			})
		};
		reader.readAsText(file);
		e.currentTarget.value = null;
		e.currentTarget.files = null;
	}
}

window.customElements.define("inspx-drawing-tool", DrawingTool);